import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, SignIn, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1664273891579-22f28332f3c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwbW9kZXJuJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzg0ODM3ODc3fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", department: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back");
        navigate("/dashboard");
      } else {
        await register(form);
        toast.success("Account created — awaiting admin approval");
        setMode("login");
        setForm({ ...form, password: "" });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="University campus" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14131c]/90 via-[#14131c]/70 to-[#14131c]/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <GraduationCap size={22} weight="fill" />
            </div>
            <span className="font-heading font-medium text-base leading-tight">HCC Employee Management System</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight leading-[1.08]">
              Daily work reporting for the modern campus.
            </h1>
            <p className="mt-6 text-white/85 leading-relaxed">
              Submit reports, track attendance, manage leave and export PDFs — all in one colorful workspace.
            </p>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-white/60">College Employee Reporting System</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-in-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <GraduationCap size={24} weight="regular" className="text-foreground" />
            <span className="font-heading font-medium text-base leading-tight">HCC Employee Management System</span>
          </div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
            {mode === "login" ? "Sign in" : "Create account"}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mt-1">
            <span className="gradient-text">{mode === "login" ? "Welcome back" : "Join your college"}</span>
          </h2>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-medium" htmlFor="name">Full name</Label>
                <Input id="name" data-testid="name-input" required value={form.name} onChange={set("name")} className="mt-1.5" placeholder="Dr. Jane Doe" />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium" htmlFor="email">Email</Label>
              <Input id="email" data-testid="email-input" type="email" required value={form.email} onChange={set("email")} className="mt-1.5" placeholder="you@college.edu" />
            </div>
            {mode === "register" && (
              <div>
                <Label className="text-xs font-medium" htmlFor="department">Department</Label>
                <Input id="department" data-testid="department-input" value={form.department} onChange={set("department")} className="mt-1.5" placeholder="Computer Science" />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium" htmlFor="password">Password</Label>
              <Input id="password" data-testid="password-input" type="password" required value={form.password} onChange={set("password")} className="mt-1.5" placeholder="••••••••" />
            </div>
            <Button type="submit" data-testid="submit-auth-btn" disabled={submitting}
              className="w-full rounded-full brand-gradient text-white border-0 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
              {mode === "login" ? <SignIn size={18} className="mr-1" /> : <UserPlus size={18} className="mr-1" />}
              {submitting ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="toggle-auth-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-medium text-primary hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
